import { useState } from "react";

import { ModalWithTitle } from "@scripts/react/components/RfModal/ModalWithTitle";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { Chip } from "@scripts/react/components/Chip";
import { unifiedTemplateSections } from "@scripts/react/components/Workflows/Templates/customModelTemplates";
import { Template } from "@scripts/react/components/Workflows/Templates/templateTypes";
import { projectSelector } from "@rflocal/shared";
import { saveWorkflowFromTemplate } from "@/utils/api/core";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@/analytics/types";
import { useCurrentWorkspace, useCurrentModelChange, useCurrentRapidData } from "@/store";
import Button from "@scripts/react/components/Button";

function shortId(): string {
    return Math.random().toString(36).slice(2, 7);
}

function transformStepToObjectDetection(step: any, specification: any) {
    const currentStepType = step.type;

    if (currentStepType.startsWith("Roboflow") && currentStepType.endsWith("Model")) {
        step.type = "RoboflowObjectDetectionModel";
    }

    if (currentStepType.includes("roboflow_core/roboflow_") && currentStepType.includes("_model")) {
        step.type = currentStepType.replace(
            /roboflow_\w+_model/,
            "roboflow_object_detection_model"
        );
    }

    // Swap PolygonVisualization → BoundingBoxVisualization for OD predictions
    specification.steps.forEach((vizStep: any) => {
        if (vizStep.predictions !== `$steps.${step.name}.predictions`) return;

        if (vizStep.type === "PolygonVisualization") {
            vizStep.type = "BoundingBoxVisualization";
        } else if (
            vizStep.type.includes("roboflow_core/") &&
            vizStep.type.includes("polygon_visualization")
        ) {
            vizStep.type = vizStep.type.replace(
                "polygon_visualization",
                "bounding_box_visualization"
            );
        }
    });
}

function compileTemplateForRapid(template: Template, modelId: string, workspaceId?: string): any {
    if (!template.specification) return template.specification;

    const spec = structuredClone(template.specification);

    // Inject model into matching userConfiguration slots
    if (template.userConfiguration) {
        template.userConfiguration.forEach((config) => {
            if (config.inputType !== "model") return;

            if (config.modelType && !config.modelType.includes("object-detection")) return;

            const step = spec.steps?.find((s: any) => s.name === config.id);
            if (!step) return;

            step.model_id = modelId;
            transformStepToObjectDetection(step, spec);
        });
    }

    // Apply global transformations (unique keys for upload/monitoring/notification blocks)
    if (spec.steps) {
        spec.steps.forEach((step: any) => {
            if (step.type.includes("roboflow_dataset_upload")) {
                step.usage_quota_name = `upload_quota_${shortId()}`;
            }
            if (step.type.includes("model_monitoring_inference_aggregator") && workspaceId) {
                step.unique_aggregator_key = `model_monitoring_aggregator_${workspaceId}_${shortId()}`;
            }
            if (step.type.includes("twilio_sms_notification") && workspaceId) {
                step.cooldown_session_key = `twilio_sms_session_${workspaceId}_${shortId()}`;
            }
            if (step.type.includes("slack_notification") && workspaceId) {
                step.cooldown_session_key = `slack_notification_session_${workspaceId}_${shortId()}`;
            }
        });
    }

    return spec;
}

function generateWorkflowUrl(name: string): string {
    return name
        .toLowerCase()
        .trim()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "")
        .replace(/-+/g, "-");
}

type TemplateDisplayConfig = {
    value: string;
    label: string;
    description: string;
    goodFor: string[];
    icons: string[];
    videoOnly?: boolean;
    previewVideo?: string;
    previewImage?: string;
};

const TEMPLATE_DISPLAY_ORDER: TemplateDisplayConfig[] = [
    {
        value: "detect-visualize-count",
        label: "Detect, Count, and Visualize",
        description:
            "Detect objects, count them automatically, and returns the count in JSON format.",
        goodFor: ["Inventory Tracking", "Crowd Analysis", "Sports Analytics"],
        icons: ["far fa-vector-square", "far fa-tally", "far fa-tag"],
        previewImage: "/videos/templates/basketball-players.png"
    },
    {
        value: "bg-removal",
        label: "Background Removal",
        description: "Remove image or video backgrounds to isolate foreground subjects.",
        goodFor: ["Product Photos", "Media Production", "Preprocessing Data"],
        icons: ["far fa-layer-group"],
        previewImage: "/videos/templates/workers.png"
    },
    {
        value: "sahi",
        label: "Small Object Detection (SAHI)",
        description:
            "Improve detection of small objects by slicing images for more accurate inference.",
        goodFor: ["Aerial Imagery", "Satellite Data", "Dense Scenes with Tiny Objects"],
        icons: ["far fa-arrow-down-short-wide"],
        previewImage: "/videos/templates/aerial-cars.png"
    },
    {
        value: "custom",
        label: "Build My Own",
        description: "Create a custom Workflow using modular building blocks.",
        goodFor: ["Custom Pipelines", "Advanced Logic", "Unique Use Cases"],
        icons: ["far fa-sitemap"],
        previewImage: "/videos/templates/build-my-own.png"
    },
    {
        value: "line-counting",
        label: "Line Counter",
        description: "Count objects as they cross a defined line in a video.",
        goodFor: ["Foot Traffic", "Vehicle Counting", "Entry/Exit Monitoring"],
        icons: ["far fa-tally"],
        videoOnly: true,
        previewVideo: "/videos/templates/line-counting.mov"
    },
    {
        value: "time-in-zone",
        label: "Time-In-Zone",
        description: "Track how long objects stay within a defined area.",
        goodFor: ["Dwell Time Analysis", "Safety Monitoring", "Sports Positioning"],
        icons: ["far fa-square-dashed", "far fa-clock"],
        videoOnly: true,
        previewVideo: "/videos/templates/time-in-zone.mp4"
    }
];

function TemplateCard({
    config,
    isSelected
}: {
    config: TemplateDisplayConfig;
    isSelected: boolean;
}) {
    return (
        <div className="group flex w-full flex-col gap-2">
            <div
                className={twClassNames(
                    "relative flex h-32 w-full flex-col items-center justify-center rounded-lg border bg-gray-50 transition-colors duration-200 ease-in-out group-hover:border-brand",
                    isSelected && "border border-brand bg-tint"
                )}
            >
                {config.videoOnly && (
                    <div className="absolute left-2 top-2">
                        <Chip
                            customColor
                            className={twClassNames(
                                "bg-gray-200 font-normal text-secondary transition-colors duration-200 ease-in-out",
                                isSelected && "bg-purboflow-200 text-brand"
                            )}
                        >
                            Video Only
                        </Chip>
                    </div>
                )}
                <div className="flex items-center gap-3">
                    {config.icons.map((icon, index) => (
                        <>
                            {index > 0 && (
                                <i
                                    key={`arrow-${icon}`}
                                    className={twClassNames(
                                        "fas fa-arrow-right text-sm text-gray-400 transition-colors duration-200 ease-in-out group-hover:text-purboflow-400",
                                        isSelected && "text-purboflow-400"
                                    )}
                                />
                            )}
                            <i
                                key={icon}
                                className={twClassNames(
                                    "text-3xl text-tertiary transition-colors duration-200 ease-in-out group-hover:text-brand",
                                    isSelected && "text-brand",
                                    icon
                                )}
                            />
                        </>
                    ))}
                </div>
            </div>
            <p className="text-base font-medium text-secondary">{config.label}</p>
        </div>
    );
}

function PreviewMedia({ type, src, alt }: { type: "video" | "image"; src: string; alt: string }) {
    const [loaded, setLoaded] = useState(false);

    return (
        <div className="flex-1">
            {type === "video" ? (
                <video
                    className={twClassNames(
                        "w-full rounded-lg object-cover transition-opacity duration-300",
                        loaded ? "opacity-100" : "opacity-0"
                    )}
                    src={src}
                    autoPlay
                    loop
                    muted
                    playsInline
                    onLoadedData={() => setLoaded(true)}
                />
            ) : (
                <img
                    className={twClassNames(
                        "w-full rounded-lg object-cover transition-opacity duration-300",
                        loaded ? "opacity-100" : "opacity-0"
                    )}
                    src={src}
                    alt={alt}
                    onLoad={() => setLoaded(true)}
                />
            )}
        </div>
    );
}

export function CreateWorkflowFromRapidModal({
    isOpen,
    onClose
}: {
    isOpen: boolean;
    onClose: () => void;
}) {
    const [loading, setLoading] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState<TemplateDisplayConfig | null>(null);
    const { track } = useAnalytics();
    const currentWorkspace = useCurrentWorkspace();
    const currentModelChange = useCurrentModelChange();
    const appBaseUrl = projectSelector.appUrl(false, true);

    const currentRapidData = useCurrentRapidData();
    const hasVideo = currentRapidData?.exampleData?.some((item) => item.isVideo) ?? false;

    const displayOrder = hasVideo
        ? [
              ...TEMPLATE_DISPLAY_ORDER.filter((t) => t.videoOnly),
              ...TEMPLATE_DISPLAY_ORDER.filter((t) => !t.videoOnly)
          ]
        : TEMPLATE_DISPLAY_ORDER;

    const modelId = currentModelChange?.to?.ref;

    const allTemplates = unifiedTemplateSections.flatMap((s) => s.templates ?? []);
    const templatesByValue = new Map(allTemplates.map((t) => [t.value, t]));

    async function handleCreate() {
        if (!selectedConfig || !currentWorkspace?.id) return;

        const template = templatesByValue.get(selectedConfig.value);
        if (!template) return;

        setLoading(true);

        try {
            let compiledSpec = template.specification;

            if (modelId) {
                compiledSpec = compileTemplateForRapid(template, modelId, currentWorkspace.id);
            }

            const url = generateWorkflowUrl(template.label);
            const payload = {
                name: template.label,
                url,
                template: template.value,
                config: JSON.stringify({ specification: compiledSpec })
            };

            const response = await saveWorkflowFromTemplate(currentWorkspace.id, payload);

            if (response?.workflow) {
                track({
                    event: AnalyticsFlowEvent.WORKFLOW_CREATED,
                    data: {
                        workflowName: template.label,
                        templateOption: template.value,
                        source: "rapid-deploy"
                    }
                });

                window.open(
                    `${appBaseUrl}/${currentWorkspace.url}/workflows/edit/${response.workflow.url}`,
                    "_blank"
                );

                onClose();
                setSelectedConfig(null);
            }
        } catch (error) {
            console.error("Error creating workflow from template", error);
        } finally {
            setLoading(false);
        }
    }

    function handleClose() {
        setSelectedConfig(null);
        onClose();
    }

    return (
        <ModalWithTitle
            isOpen={isOpen}
            onClose={handleClose}
            title="Add Intelligence & Automation"
            icon="far fa-cubes"
            showFooter={false}
            size="xxxl"
            className="h-max-[640px] p-0"
        >
            <div className="flex h-full">
                {/* Left rail: flat template grid */}
                <div className="flex w-[480px] flex-shrink-0 overflow-y-auto border-r border-divider p-6">
                    <div className="grid grid-cols-2 items-start gap-x-4 gap-y-5">
                        {displayOrder.map((config) => (
                            <button
                                key={config.value}
                                onClick={() => {
                                    setSelectedConfig(config);
                                    track({
                                        event: AnalyticsFlowEvent.WORKFLOW_TEMPLATE_SELECTED,
                                        data: {
                                            templateOption: config.value,
                                            templateName: config.label
                                        }
                                    });
                                }}
                                className="w-full text-left focus:outline-none"
                            >
                                <TemplateCard
                                    config={config}
                                    isSelected={selectedConfig?.value === config.value}
                                />
                            </button>
                        ))}
                    </div>
                </div>

                {/* Right pane: template details */}
                <div className="flex w-full flex-col">
                    <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-6">
                        {selectedConfig ? (
                            <div className="flex h-full flex-col justify-evenly gap-6">
                                <div>
                                    <h2 className="text-lg font-medium text-secondary">
                                        {selectedConfig.label}
                                    </h2>
                                    <p className="text-sm text-tertiary">
                                        {selectedConfig.description}
                                    </p>
                                </div>
                                {/* Preview */}
                                <div className="flex items-center rounded-xl border-2 border-dashed border-purboflow-200 bg-tint p-3">
                                    {selectedConfig.previewVideo ? (
                                        <PreviewMedia
                                            key={selectedConfig.value}
                                            type="video"
                                            src={selectedConfig.previewVideo}
                                            alt={selectedConfig.label}
                                        />
                                    ) : selectedConfig.previewImage ? (
                                        <PreviewMedia
                                            key={selectedConfig.value}
                                            type="image"
                                            src={selectedConfig.previewImage}
                                            alt={selectedConfig.label}
                                        />
                                    ) : (
                                        <div className="flex flex-1 items-center justify-center rounded-lg border-2 border-dashed border-purple-300 bg-gray-50 py-20">
                                            <span className="text-sm text-gray-400">Preview</span>
                                        </div>
                                    )}
                                </div>
                                {/* Good for tags */}
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-tertiary">Good for:</span>
                                    {selectedConfig.goodFor.map((tag) => (
                                        <Chip key={tag}>{tag}</Chip>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="flex h-full items-center justify-center text-sm text-gray-400">
                                Select a template to preview
                            </div>
                        )}
                    </div>
                    {selectedConfig && (
                        <div className="flex w-full justify-end border-t px-6 py-3">
                            <Button
                                onClick={handleCreate}
                                type="primary"
                                loading={loading}
                                disabled={!selectedConfig || loading}
                            >
                                Continue
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </ModalWithTitle>
    );
}
